"use client";

import { useEffect } from "react";
import { cn } from "@/utils/utils";
import React, { useState } from "react";
import useTranslationApi from "@/lib/hooks/useTranslationApi";

import ImageConstant from "@/common/constants/image";
import ProductModel from "@/common/models/ProductModel";
import { ProductAllowSale, ProductJson } from "@/types/Product.type";

import Image from "@/components/ui/Image";
import AddToCartBtn from "~components/cart/AddToCartBtn";
import WishlistBtn from "~components/wishlist/WishlistBtn";
import ProductBtnSendMail from "~components/product/ProductBtnSendMail";
import ProductSinglePrice from "~components/product/ProductSinglePrice";
import ProductVariantList from "~components/product/ProductVariantList";

interface ProductStickyProps {
  product: ProductJson;
  productVariants: ProductJson[];
  quantity: number;
  productVariantActive: ProductJson;
  onClickProductVariant: (productVariant: ProductJson) => void;
  store_id: number;
}

function ProductSticky({
  product,
  quantity,
  productVariantActive,
  onClickProductVariant,
  store_id,
  productVariants,
}: ProductStickyProps) {
  const [isShow, setIsShow] = useState(false);
  const { t } = useTranslationApi(["product_not_allow_sale"]);

  function handlerScroll() {
    // set to self estimate
    const OFFSET_Y = 100;
    if (window.scrollY > OFFSET_Y) {
      setIsShow(true);
    } else {
      setIsShow(false);
    }
  }

  function handleShowNameOption(name: string) {
    const res = name.split("-");
    const nameOption = res[res.length - 1];

    return nameOption;
  }

  useEffect(() => {
    window.addEventListener("scroll", handlerScroll);
    return () => {
      window.removeEventListener("scroll", handlerScroll);
    };
  }, []);

  return (
    <div
      className={cn(
        "fixed hidden sm:block top-0 left-0 right-0 z-[999] bg-white -translate-y-full transition-all duration-200 shadow-md",
        {
          "translate-y-0": isShow,
        }
      )}
    >
      <div className="container flex items-center justify-between py-4">
        {/* Info */}
        <div className="flex flex-1 items-center gap-[10px] flex-shrink-0 max-w-[35%]">
          <Image
            alt={product.name}
            width={40}
            height={40}
            src={productVariantActive?.images[0]?.url}
            placeholder={"blur"}
            blurDataURL={ImageConstant.BlurDataURL}
          />
          <div>
            <h3 className="font-bold text-sm text-gray-500 uppercase mb-[2px] line leading-[1.6] line-clamp-1">
              {product.name}
            </h3>
            <p className="text-[12px] text-gray-400 capitalize line-clamp-1">
              {productVariantActive.option_name ||
                productVariantActive.full_name}
            </p>
          </div>
        </div>

        {/* Variant */}
        <div className="max-w-[400px] min-w-[280px] flex-1 overflow-auto">
          {productVariants.length > 1 && (
            <ProductVariantList
              variantActive={productVariantActive}
              variants={productVariants}
              className="flex gap-2"
              renderItem={(item) => {
                return (
                  <li
                    className={cn(
                      "border cursor-pointer border-gray-500 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center p-1",
                      {
                        " border-red-500": item.id === productVariantActive.id,
                      }
                    )}
                    onClick={() => onClickProductVariant(item)}
                  >
                    <span
                      className={cn(
                        "cursor-pointer text-xs block w-full text-center whitespace-nowrap overflow-hidden  text-ellipsis",
                        {
                          "line-through": item.quantity  <= 0,
                        }
                      )}
                    >
                      {item.full_name
                        ? handleShowNameOption(item.full_name)
                        : item.full_name}
                    </span>
                  </li>
                );
              }}
            />
          )}
        </div>

        {/* Price & Actions */}
        {productVariantActive.allow_sale === ProductAllowSale.ALLOW_SALE ? (
          <div className="flex flex-1 gap-4 justify-between items-center">
            <ProductSinglePrice
              className="gap-[2px] flex-col-reverse items-center"
              showDiscount
              text=""
              productVariant={productVariantActive}
              classNamePriceCompare="h-[15px] leading-[15px] text-gray-400"
              classNamePrice="h-[26px] leading-[26px]"
            />
            {productVariantActive.quantity > 0 ? (
              <AddToCartBtn
                productData={productVariantActive}
                isCheckInCart={true}
                item_quantity={quantity}
                mode="add"
                store_id={store_id}
                className="max-w-[158px]"
              />
            ) : (
              <ProductBtnSendMail productVariantActive={productVariantActive} />
            )}
            <WishlistBtn
              model={{
                item_id: product.parent_id > 0 ? product.parent_id : product.id,
                item_type: 1,
                item_url: product.handle,
              }}
            />
          </div>
        ) : (
          <h4 className="text-red-500 font-bold text-lg">
            {t("product_not_allow_sale")}
          </h4>
        )}
      </div>
    </div>
  );
}

export default ProductSticky;
